import { Injectable, inject } from '@angular/core';
import { TenantService, UpdateTenantUserCommand, UserTenantInformation } from '@api';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { Navigate } from '@ngxs/router-plugin';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { TENANT_ID } from '../../authorization/authorization-factories';
import { AcceptDisclaimer, Login, Logout, SetTenantUser, ToggleSideBar, UserLoggedIn, UserLoggedOut } from './application.actions';

export class ApplicationStateModel {
    public tenantName: string;
    public currentAadUser: AccountInfo;
    public isUserLoggedIn: boolean;
    public sidebarIsCollapsed: boolean;
    public userTenantInformation: UserTenantInformation;
}

@State<ApplicationStateModel>({
    name: 'application',
    defaults: {
        currentAadUser: null,
        tenantName: null,
        isUserLoggedIn: false,
        sidebarIsCollapsed: false,
        userTenantInformation: null,
    },
})
@Injectable({ providedIn: 'root' })
export class ApplicationState {
    private authService = inject(MsalService);
    private tenantId = inject(TENANT_ID);
    private tenantService = inject(TenantService);

    @Selector()
    public static isUserLoggedIn(state: ApplicationStateModel) {
        return state.isUserLoggedIn;
    }

    @Selector()
    public static currentUserName(state: ApplicationStateModel) {
        return decodeURIComponent(state?.currentAadUser?.name);
    }

    @Selector()
    public static tenantName(state: ApplicationStateModel) {
        return decodeURIComponent(state?.tenantName);
    }

    @Selector()
    public static currentUser(state: ApplicationStateModel) {
        return state.currentAadUser;
    }

    @Selector()
    public static sidebarIsCollapsed(state: ApplicationStateModel) {
        return state.sidebarIsCollapsed;
    }

    @Selector()
    public static userTenant(state: ApplicationStateModel) {
        return state.userTenantInformation;
    }

    @Selector()
    public static permissions(state: ApplicationStateModel) {
        return state.userTenantInformation?.permissions as Permissions;
    }

    @Selector()
    public static features(state: ApplicationStateModel) {
        return state.userTenantInformation?.features;
    }

    @Selector()
    public static hasAcceptedDisclaimer(state: ApplicationStateModel) {
        return state.userTenantInformation?.user?.hasAcceptedSecureEnvironmentDisclaimer ?? false;
    }

    @Action(Login)
    public login({ dispatch }: StateContext<ApplicationStateModel>) {
        dispatch(new Navigate(['./login']));
    }

    @Action(Logout)
    public logout() {
        const activeAccount = this.authService.instance.getActiveAccount() || this.authService.instance.getAllAccounts()?.[0];
        this.authService.logoutRedirect({ account: activeAccount });
    }

    @Action(UserLoggedIn)
    public userLoggedIn({ patchState }: StateContext<ApplicationStateModel>, action: UserLoggedIn) {
        if (!action.user) {
            return;
        }

        patchState({
            currentAadUser: action.user,
            isUserLoggedIn: true,
            tenantName: this.tenantId,
        });
    }

    @Action(UserLoggedOut)
    public userLoggedOut({ setState }: StateContext<ApplicationStateModel>) {
        setState(null);
    }

    @Action(ToggleSideBar)
    public toggleSideBar({ patchState, getState }: StateContext<ApplicationStateModel>) {
        const state = getState();
        patchState({ sidebarIsCollapsed: !state.sidebarIsCollapsed });
    }

    @Action(SetTenantUser)
    public setTenantUser(ctx: StateContext<ApplicationStateModel>, action: SetTenantUser) {
        const tenant = action.userTenantInformation.tenant;
        if ((tenant.name?.trim() ?? '') === '') {
            tenant.name = 'achtBytes IoT';
        }

        ctx.patchState({ userTenantInformation: action.userTenantInformation });
    }

    @Action(AcceptDisclaimer)
    public acceptDisclaimer({ getState }: StateContext<ApplicationStateModel>, action: AcceptDisclaimer) {
        const tenantName = getState().tenantName;
        return this.tenantService.updateTenantUser(tenantName, {
            hasAcceptedSecureEnvironmentDisclaimer: true,
        } as UpdateTenantUserCommand);
    }
}
